import AdvancedDestinationSelectorV2 from './advanced-destination-selector-v2';

export default class AdvancedDestinationSelectorV3 extends AdvancedDestinationSelectorV2 {
    constructor(form, destinationCodeInput) {
        super(form, destinationCodeInput);
        this._hierarchyStructure = null;
        this._setupHierarchicalData();
        this._mobileBreakpoint = 569;
        window.addEventListener('resize', () => this._positionDropdownToWidget());
    }

    _sanitize(value) {
        return this.constructor._sanitizeString(String(value || ''));
    }

    _joinSanitized(...parts) {
        return parts.filter(Boolean).map(p => this._sanitize(p)).join(' ');
    }

    static _buildHotelObject(h, context) {
        const { cityName, provName, countryName } = context,
            sanitize = s => this._sanitizeString(String(s || '')),
            code = Array.isArray(h) ? h[0] : h.code,
            name = Array.isArray(h) ? h[1] : h.name,
            stars = Array.isArray(h) ? h[2] : h.hotelCategory,
            parts = [name, code, stars, cityName, provName, countryName].filter(Boolean),
            hotelSearch = parts.map(sanitize).join(' ');
        return {
            code: code || sanitize(name),
            name: name || '',
            hotelCategory: stars || '',
            searchText: hotelSearch,
            isHotel: true,
            city: cityName
        };
    }

    static _normalizeProvincesList(provinces) {
        if (Array.isArray(provinces)) {
            return provinces;
        }
        return provinces ? [provinces] : [];
    }

    _setupHierarchicalData() {
        try {
            const raw = JSON.parse(this._container.dataset.destinations);
            this._hierarchyStructure = {};

            if (Array.isArray(raw)) {
                raw.forEach(region => this._setupCountryFromRegion(region));
            } else if (raw && typeof raw === 'object') {
                this._setupHierarchicalFromObject(raw);
            } else {
                this._hierarchyStructure = {};
            }
        } catch (e) {
            this._hierarchyStructure = {};
        }
    }

    _setupCountryFromRegion(region) {
        const countryData = this.constructor._extractCountryData(region),
            countryCode = this._resolveCode(region, countryData),
            countryName = region.name || countryData.name || '',
            countryObj = this._hierarchyStructure[countryCode] || {
                name: countryName,
                code: countryCode,
                searchText: this._joinSanitized(
                    countryName, countryCode, countryData.name, countryData.code
                ),
                provinces: {}
            },
            provinces = this.constructor._normalizeProvincesList(countryData.provinces);

        provinces.forEach((prov) => {
            this._setupProvinceInCountry(prov, { countryObj, countryName });
        });

        this._hierarchyStructure[countryCode] = countryObj;
    }

    _resolveCode(region, countryData) {
        return region.code || countryData.code || this._sanitize(region.name) ||
            this._sanitize(countryData.name) || `country_${Math.random()}`;
    }

    static _extractCountryData(region) {
        const isDirectFormat = region.provinces !== undefined;
        return isDirectFormat ? region : (region.country || {});
    }

    _setupProvinceInCountry(prov, context) {
        const { countryObj, countryName } = context,
            provName = prov.name || '',
            provCode = prov.code ||
            this._sanitize(provName) ||
            `prov_${Math.random()}`,
            provObj = countryObj.provinces[provCode] || {
                name: provName,
                code: provCode,
                searchText: this._joinSanitized(provName, provCode, countryName),
                cities: {}
            };

        let citiesRaw = [];
        if (prov.cities) {
            citiesRaw = Array.isArray(prov.cities)
                ? prov.cities
                : [prov.cities];
        }

        citiesRaw.forEach((city) => {
            this._setupCityInProvince(city, { provObj, provName, countryName });
        });

        countryObj.provinces[provCode] = provObj;
    }

    _setupCityInProvince(city, context) {
        const { provObj, provName, countryName } = context,
            cityName = city.name || '',
            cityCode = city.code ||
            this._sanitize(cityName) ||
            `city_${Math.random()}`,
            hotelsRaw = Array.isArray(city.hotels) ? city.hotels : [],
            hotels = hotelsRaw.map(h => this.constructor._buildHotelObject(h, {
                cityName,
                provName,
                countryName
            }));

        if (provObj.cities[cityCode]) {
            provObj.cities[cityCode].hotels =
                provObj.cities[cityCode].hotels.concat(hotels);
        } else {
            provObj.cities[cityCode] = {
                name: cityName,
                code: cityCode,
                searchText: this._joinSanitized(cityName, cityCode, provName, countryName),
                hotels
            };
        }
    }

    _setupHierarchicalFromObject(raw) {
        Object.keys(raw).forEach((cKey) => {
            const country = raw[cKey] || {};
            country.searchText = country.searchText ||
                this._joinSanitized(country.name, country.code);
            country.provinces = country.provinces || {};
            Object.keys(country.provinces).forEach((pKey) => {
                const prov = country.provinces[pKey] || {};
                prov.searchText = prov.searchText ||
                    this._joinSanitized(prov.name, prov.code);
                prov.cities = prov.cities || {};
                Object.keys(prov.cities).forEach((ciKey) => {
                    const city = prov.cities[ciKey] || {};
                    city.searchText = city.searchText ||
                        this._joinSanitized(city.name, city.code, prov.name);
                    this._normalizeHotelsInCity(city, {
                        provName: prov.name,
                        countryName: country.name
                    });
                });
            });
            this._hierarchyStructure[cKey] = country;
        });
    }

    // eslint-disable-next-line class-methods-use-this
    _formatDestinations() {
        return { destinations: [], hotels: [] };
    }

    _normalizeHotelsInCity(city, context) {
        const hotelsRaw = Array.isArray(city.hotels)
            ? city.hotels
            : [];
        city.hotels = hotelsRaw.map((h) => {
            if (h.searchText) {
                return h;
            }
            return this.constructor._buildHotelObject(h, {
                cityName: city.name,
                provName: context.provName,
                countryName: context.countryName
            });
        });
    }

    filterOptions(openFlag = false) {
        this._clear();
        let enteredText = this._mainInput.value,
            results = {};
        const selectedCodes = this._getSelectedHotelCodes(),
            isSelectAllHotels = this._coddestino.value === 'todos';

        if (enteredText === '' || openFlag || isSelectAllHotels) {
            if (enteredText === '') {
                this._coddestino.value = '';
            }
            results = this._getFullHierarchy();
        } else if (selectedCodes.length > 1) {
            results = this._searchHierarchyByHotelCodes(selectedCodes);
        } else {
            enteredText = this.constructor._sanitizeString(enteredText);
            results = this._searchHierarchy(enteredText);
        }

        this._populateHierarchicalContainer(results);
        this._mainInput.value = this._mainInput.value.replace(/^[\s-]+/g, '');
        this._container.classList.add(this._visibleClass);
        this._positionDropdownToWidget();
    }

    _positionDropdownToWidget() {
        if (window.innerWidth < this._mobileBreakpoint) {
            this._container.style.left = '';
            this._container.style.width = '';
            return;
        }
        const formRect = this._form.getBoundingClientRect(),
            itemContainer = this._container.parentElement,
            itemRect = itemContainer.getBoundingClientRect();
        this._container.style.left = `${formRect.left - itemRect.left}px`;
        this._container.style.width = `${formRect.width}px`;
    }

    _getFullHierarchy() {
        return { ...this._hierarchyStructure };
    }

    _getSelectedHotelCodes() {
        const selectedValue = this._coddestino.value;
        return (!selectedValue || !selectedValue.includes(','))
            ? []
            : selectedValue.split(',').map(code => code.trim()).filter(Boolean);
    }

    _searchHierarchyByHotelCodes(selectedCodes) {
        const selectedSet = new Set(selectedCodes),
            results = {};

        Object.keys(this._hierarchyStructure).forEach((countryKey) => {
            const country = this._hierarchyStructure[countryKey],
                matchedCountry = this._matchCountryBySelectedHotels(country, selectedSet);
            if (matchedCountry) {
                results[countryKey] = matchedCountry;
            }
        });

        return results;
    }

    _matchCountryBySelectedHotels(country, selectedSet) {
        const matchedProvinces = {};
        Object.keys(country.provinces || {}).forEach((provKey) => {
            const prov = this.constructor._matchProvinceBySelectedHotels(country.provinces[provKey], selectedSet);
            if (prov) {
                matchedProvinces[provKey] = prov;
            }
        });
        return Object.keys(matchedProvinces).length === 0 ? null : { ...country, provinces: matchedProvinces };
    }

    static _matchProvinceBySelectedHotels(prov, selectedSet) {
        const matchedCities = {};
        Object.keys(prov.cities || {}).forEach((cityKey) => {
            const matchedHotels = (prov.cities[cityKey].hotels || []).filter(h => selectedSet.has(h.code));
            if (matchedHotels.length > 0) {
                matchedCities[cityKey] = { ...prov.cities[cityKey], hotels: matchedHotels };
            }
        });
        return Object.keys(matchedCities).length === 0 ? null : { ...prov, cities: matchedCities };
    }

    _searchHierarchy(searchText) {
        const results = {};

        Object.keys(this._hierarchyStructure).forEach((countryKey) => {
            const country = this._hierarchyStructure[countryKey],
                countryMatches = country.searchText && country.searchText.includes(searchText),
                matchedProvinces = {};

            Object.keys(country.provinces || {}).forEach((provKey) => {
                const matched = this._matchProvinceBySearch(country.provinces[provKey], searchText);
                if (matched) {
                    matchedProvinces[provKey] = matched;
                }
            });

            if (countryMatches || Object.keys(matchedProvinces).length > 0) {
                results[countryKey] = { ...country, provinces: matchedProvinces };
            }
        });

        return results;
    }

    _matchProvinceBySearch(prov, searchText) {
        const provMatches = prov.searchText && prov.searchText.includes(searchText),
            matchedCities = {};

        Object.keys(prov.cities || {}).forEach((cityKey) => {
            const city = prov.cities[cityKey],
                match = this.constructor._matchCity(city, searchText);
            if (match.cityMatches || match.matchedHotels.length > 0) {
                matchedCities[cityKey] = {
                    ...city,
                    hotels: match.cityMatches ? city.hotels : match.matchedHotels
                };
            }
        });

        return (provMatches || Object.keys(matchedCities).length > 0)
            ? { ...prov, cities: matchedCities }
            : null;
    }

    static _matchCity(city, searchText) {
        const cityMatches = city.searchText && city.searchText.includes(searchText),
            matchedHotels = [];
        (city.hotels || []).forEach((hotel) => {
            if (hotel.searchText && hotel.searchText.includes(searchText)) {
                matchedHotels.push(hotel);
            }
        });
        return { cityMatches, matchedHotels };
    }

    _populateHierarchicalContainer(hierarchy) {
        const resultsList = this._container.querySelector(
            '.js-roi-search-engine-destination-results-list'
        );

        if (!resultsList) {
            return;
        }

        const fragment = document.createDocumentFragment();

        if (Object.keys(hierarchy).length === 0) {
            fragment.appendChild(
                this._destinationResults.createNoResultsMessage()
            );
        } else {
            Object.keys(hierarchy).sort((a, b) => hierarchy[a].name.localeCompare(hierarchy[b].name)).forEach((ck) => {
                const country = hierarchy[ck],
                    countryHeader = this.constructor._createHierarchySection(
                        country.name,
                        'country',
                        country.code || ck
                    ),
                    provinceList = document.createElement('ul'),
                    provModifier = 'roi-search-engine__destination-results-item__list--province';
                provinceList.className =
                    `${this._getListClassNameForLevel('destination')} ${provModifier}`;

                const provs = country.provinces;
                Object.keys(provs).sort((a, b) => provs[a].name.localeCompare(provs[b].name)).forEach((pk) => {
                    provinceList.appendChild(this._buildProvinceSection(provs[pk]));
                });

                countryHeader.appendChild(provinceList);
                fragment.appendChild(countryHeader);
            });
        }

        resultsList.appendChild(fragment);
        this._destinationResults.listenToResultsEvents();
    }

    _getListClassNameForLevel(level) {
        const baseClass = 'roi-search-engine__destination-results-item__list',
            hasIcons = this._container.dataset.destinationShowIcons !== undefined;

        if (!hasIcons) {
            return baseClass;
        }

        const iconClass = level === 'hotel'
            ? 'roi-search-engine__destination-results-list--hotels-with-icon'
            : 'roi-search-engine__destination-results-list--destinations-with-icon';

        return `${baseClass} ${iconClass}`;
    }

    _buildProvinceSection(province) {
        const pc = province.cities,
            cities = Object.keys(pc).sort((a, b) => pc[a].name.localeCompare(pc[b].name)),
            shouldHideProvince = cities.length === 1 && pc[cities[0]].name === province.name;

        if (shouldHideProvince) {
            return this._buildCityWithHotels(pc[cities[0]]);
        }

        const provinceItem = this.constructor._createHierarchyItem(
                province.name,
                province.code,
                { level: 'province', stars: false }
            ),
            cityList = document.createElement('ul');
        cityList.className = this._getListClassNameForLevel('destination');

        cities.forEach((cityKey) => {
            cityList.appendChild(this._buildCityWithHotels(pc[cityKey]));
        });

        provinceItem.appendChild(cityList);
        return provinceItem;
    }

    _buildCityWithHotels(city) {
        const cityItem = this.constructor._createHierarchyItem(
                city.name,
                city.code,
                { level: 'city', stars: false }
            ),
            hotelList = document.createElement('ul');
        hotelList.className = this._getListClassNameForLevel('hotel');

        if (Array.isArray(city.hotels)) {
            city.hotels.forEach((hotel) => {
                const hotelItem = this._destinationResults.createResult(hotel, 'hotels', false);
                hotelList.appendChild(hotelItem);
            });
        }
        cityItem.appendChild(hotelList);
        return cityItem;
    }

    static _createHierarchySection(name, level, code) {
        const section = document.createElement('li'),
            baseCls = 'roi-search-engine__destination-hierarchy-section',
            resultCls = 'roi-search-engine__destination-results-item',
            levelCls = `${baseCls}--${level}`;
        section.className = `${baseCls} ${resultCls} ${levelCls}`;
        section.tabIndex = 0;
        section.innerText = name;
        section.dataset.level = level;
        section.dataset.name = name;
        if (code) {
            section.dataset.code = code;
        }
        return section;
    }

    static _createHierarchyItem(name, code, opts = {}) {
        const { stars = false, level = null } = opts,
            item = document.createElement('li'),
            baseClass = 'roi-search-engine__destination-results-item',
            hierarchyClass = `${baseClass}--hierarchy`;
        item.className = `${baseClass} ${hierarchyClass}`;
        item.tabIndex = 0;
        item.dataset.code = code;
        item.dataset.name = name;
        if (level) {
            item.dataset.level = level;
        }
        item.innerText = name;

        if (stars) {
            const starsEl = document.createElement('span');
            starsEl.className = 'roi-search-engine__destination-results-item__stars';
            starsEl.innerText = stars;
            item.appendChild(starsEl);
        }

        return item;
    }

    selectChoice(choice) {
        const { code, name } = choice.dataset,
            selectedName = name || choice.textContent.trim();

        this._mainInput.value = selectedName;
        this._coddestino.value = code;
        this._handleHotelSelection(code);
        this._close();
    }

    _handleHotelSelection(code) {
        if (this._destinationPrefixes.includes(code[0])) {
            const occupancyContainer = document.querySelector(
                '.js-roi-search-engine-occupancy-container'
            );
            if (occupancyContainer) {
                occupancyContainer.style.display = 'none';
            }
        } else {
            this._coddestino.dispatchEvent(new CustomEvent('input', {
                bubbles: true,
                cancelable: true
            }));
        }
    }
}
